import { useLocalization } from '@faceup/localization'
import { FormItemType, getTranslation, type Language } from '@faceup/utils'
import moment from 'moment-timezone'
import { type FragmentType, getFragmentData, graphql } from '../__generated__'
import type { FormItemAnswer } from '../components'

const fragments = {
  useFormItemsAnswers_formItemAnswer: graphql(`
    fragment useFormItemsAnswers_formItemAnswer on FormItemAnswer {
      id
      values
      attachments {
        id
        name
        url
        mimetype
      }
      translatedValues: values(sourceLanguage: $sourceLanguage, targetLanguage: $targetLanguage)
      formItem {
        id
        formItemId
        type
        labelTranslations {
          language
          translation
        }
        options(includeDeleted: true, includeOtherOptions: true) {
          id
          order
          labelTranslations(sourceLanguage: $sourceLanguage, targetLanguage: $targetLanguage) {
            language
            translation
          }
        }
        scaleStartLabelTranslations {
          language
          translation
        }
        scaleEndLabelTranslations {
          language
          translation
        }
        matrixConfig {
          options {
            id
            labelTranslations {
              language
              translation
            }
          }
          questions {
            id
            labelTranslations {
              language
              translation
            }
          }
        }
      }
      matrixValues {
        optionId
        questionId
      }
    }
  `),
}

export const useFormItemsAnswers = () => {
  const { language } = useLocalization()
  return (
    _answers: FragmentType<typeof fragments.useFormItemsAnswers_formItemAnswer>[],
    defaultLanguage?: Language
  ): FormItemAnswer[] =>
    [...getFragmentData(fragments.useFormItemsAnswers_formItemAnswer, _answers)].map(answer => {
      const getFormItemAnswerWithCustomValue = ({
        answer,
        value,
      }: {
        answer: NonNullable<
          FragmentType<typeof fragments.useFormItemsAnswers_formItemAnswer>[' $fragmentRefs']
        >['useFormItemsAnswers_formItemAnswerFragment']
        value: string
      }) => {
        return {
          id: answer.id,
          formItemId: answer.formItem.formItemId,
          type: answer.formItem.type,
          label: getTranslation(answer.formItem.labelTranslations, language, defaultLanguage),
          value,
          attachments: answer.attachments,
        }
      }

      const getSelectFormItemAnswerWithCustomValue = () => {
        const optionTranslations =
          answer.formItem.options.find(option => option.id === answer.values[0])
            ?.labelTranslations ?? []

        const optionTranslationValue = getTranslation(optionTranslations, language, defaultLanguage)

        return getFormItemAnswerWithCustomValue({
          answer,
          value: optionTranslationValue,
        })
      }

      const getMultiSelectFormItemAnswerWithCustomValue = () => {
        const translatedMultiValues = answer.translatedValues ?? []
        const translatedOptions = answer.formItem.options.map(option => ({
          id: option.id,
          label: getTranslation(option.labelTranslations, language, defaultLanguage),
        }))
        const selectedOptions = translatedOptions.filter(option =>
          translatedMultiValues.includes(option.id)
        )
        return getFormItemAnswerWithCustomValue({
          answer,
          value: selectedOptions.map(option => option.label).join(', '),
        })
      }

      const getMatrixValue = ({
        answer,
      }: {
        answer: NonNullable<
          FragmentType<typeof fragments.useFormItemsAnswers_formItemAnswer>[' $fragmentRefs']
        >['useFormItemsAnswers_formItemAnswerFragment']
      }) => {
        const optionsWithAnswers = answer.matrixValues.reduce(
          (acc, { optionId, questionId }) => {
            if (!acc[questionId]) {
              acc[questionId] = []
            }
            acc[questionId].push(optionId)
            return acc
          },
          {} as Record<string, string[]>
        )

        // Format the output to show each question and its selected options
        const formattedValue = Object.entries(optionsWithAnswers)
          .map(([questionId, optionIds]) => {
            // Get question translation
            const question = answer.formItem.matrixConfig?.questions.find(q => q.id === questionId)
            const questionLabel = question
              ? getTranslation(question.labelTranslations, language, defaultLanguage)
              : questionId

            // Get option translations
            const optionLabels = optionIds.map(optionId => {
              const option = answer.formItem.matrixConfig?.options.find(opt => opt.id === optionId)
              return option
                ? getTranslation(option.labelTranslations, language, defaultLanguage)
                : optionId
            })

            return `${questionLabel}: ${optionLabels.join(', ')}`
          })
          .join('\n')

        return {
          id: answer.id,
          formItemId: answer.formItem.formItemId,
          type: answer.formItem.type,
          label: getTranslation(answer.formItem.labelTranslations, language, defaultLanguage),
          value: formattedValue,
        }
      }

      const getFormItemAnswerWithSimpleValue = ({
        answer,
      }: {
        answer: NonNullable<
          FragmentType<typeof fragments.useFormItemsAnswers_formItemAnswer>[' $fragmentRefs']
        >['useFormItemsAnswers_formItemAnswerFragment']
      }) => getFormItemAnswerWithCustomValue({ answer, value: answer.translatedValues[0] ?? '' })

      const getScaleFormItemAnswerWithCustomValue = ({
        answer,
        value,
        labelExtra,
      }: {
        answer: NonNullable<
          FragmentType<typeof fragments.useFormItemsAnswers_formItemAnswer>[' $fragmentRefs']
        >['useFormItemsAnswers_formItemAnswerFragment']
        value: string
        labelExtra: string
      }) => {
        return {
          id: answer.id,
          formItemId: answer.formItem.formItemId,
          type: answer.formItem.type,
          label: `${getTranslation(answer.formItem.labelTranslations, language, defaultLanguage)} ${labelExtra}`,
          value,
        }
      }

      const formItems: Record<FormItemType, () => FormItemAnswer> = {
        [FormItemType.Scale]: () => {
          const startLabel = answer.formItem.scaleStartLabelTranslations ?? []
          const endLabel = answer.formItem.scaleEndLabelTranslations ?? []
          const startTranslation = getTranslation(startLabel, language, defaultLanguage)
            ? `-${getTranslation(startLabel, language, defaultLanguage)}`
            : ''
          const endTranslation = getTranslation(endLabel, language, defaultLanguage)
            ? `-${getTranslation(endLabel, language, defaultLanguage)}`
            : ''
          const optionsWithOrder = answer.formItem.options.filter(option => option.order !== null)
          const labelExtra = `${answer.formItem.options[0]?.labelTranslations[0]?.translation}${startTranslation}${endTranslation && startTranslation ? ', ' : '-'}${optionsWithOrder[optionsWithOrder.length - 1]?.labelTranslations[0]?.translation}${endTranslation}`

          const optionId = answer.values[0] ?? ''
          const option = answer.formItem.options.find(option => option.id === optionId)
          const value =
            getTranslation(option?.labelTranslations ?? [], language, defaultLanguage) ?? optionId

          return getScaleFormItemAnswerWithCustomValue({
            answer,
            value,
            labelExtra: `(${labelExtra})`,
          })
        },
        [FormItemType.Rating]: () => {
          const optionsWithOrder = answer.formItem.options.filter(option => option.order !== null)
          const labelExtra = `1 - ${optionsWithOrder.length}`

          const optionId = answer.values[0] ?? ''
          const option = answer.formItem.options.find(option => option.id === optionId)
          const value =
            getTranslation(option?.labelTranslations ?? [], language, defaultLanguage) ?? optionId

          return getScaleFormItemAnswerWithCustomValue({
            answer,
            value,
            labelExtra: `(${labelExtra})`,
          })
        },
        [FormItemType.Select]: getSelectFormItemAnswerWithCustomValue,
        [FormItemType.MultiSelect]: getMultiSelectFormItemAnswerWithCustomValue,
        [FormItemType.SimpleText]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.MultilineText]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.Date]: () =>
          getFormItemAnswerWithCustomValue({ answer, value: moment(answer.values[0]).format('L') }),
        [FormItemType.MoreInformation]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.Classroom]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.OrganizationalUnit]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.SenderName]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.Email]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.PhoneNumber]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.Category]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.Number]: () => getFormItemAnswerWithSimpleValue({ answer }),
        [FormItemType.File]: () => {
          return getFormItemAnswerWithCustomValue({
            answer,
            value: answer.attachments.map(attachment => attachment.name).join(','),
          })
        },
        [FormItemType.Matrix]: () => getMatrixValue({ answer }),
      }

      return formItems[answer.formItem.type]()
    })
}
